import { useEffect } from 'react';
import Icon from './Icon';

export function EmptyState({ text, icon = 'people', action }) {
  return (
    <div className="empty">
      <Icon name={icon} size={40} className="empty__icon" />
      <p className="empty__text">{text}</p>
      {action}
    </div>
  );
}

export function ErrorBanner({ error, onRetry }) {
  if (!error) return null;
  return (
    <div className="banner banner--error" role="alert">
      <Icon name="alert" size={18} className="banner__icon" />
      <div className="banner__body">
        <p className="banner__title">Something went wrong</p>
        <pre className="banner__detail">{String(error)}</pre>
      </div>
      {onRetry && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }) {
  return (
    <div className="spinner" role="status">
      <span className="spinner__ring" />
      <span className="spinner__label">{label}</span>
    </div>
  );
}

export function Stepper({ value, onChange, min = 0, max = 99, ariaLabel }) {
  return (
    <div className="stepper">
      <button
        type="button"
        className="stepper__btn stepper__btn--minus"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label={`Decrease ${ariaLabel || 'value'}`}
      >
        <Icon name="minus" size={16} />
      </button>
      <span className="stepper__value" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="stepper__btn stepper__btn--plus"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label={`Increase ${ariaLabel || 'value'}`}
      >
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}

export function Modal({ title, subtitle, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="modal__scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'modal--wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal__header">
          <div>
            <h2 className="modal__title">{title}</h2>
            {subtitle && <p className="modal__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="x" size={18} />
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </div>
  );
}
